import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { BookOpen, FileText, Quote, StickyNote, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AddNoteModal } from "@/components/notes/AddNoteModal";
import {
  CollectionEmpty,
  CollectionList,
  CollectionLoading,
  CollectionPage,
  CollectionRow,
  FilterInput,
} from "@/components/layout/CollectionPage";
import { get } from "@/lib/api";
import { stripMarkdown } from "@/lib/markdown";
import type { Note } from "@/types";

type NoteType = "all" | "standalone" | "scripture" | "quote" | "document";

const TYPE_LABELS: Record<NoteType, string> = {
  all: "Any kind",
  standalone: "Loose notes",
  scripture: "On scripture",
  quote: "On quotes",
  document: "On documents",
};

/** The note's own first line — what tells one note from another. */
function noteHeadline(note: Note) {
  const [firstLine] = stripMarkdown(note.content).trim().split("\n");
  return firstLine?.trim() || "Empty note";
}

/** Where it came from. Context, so it sits under the headline, not above it. */
function noteSource(note: Note) {
  switch (note.parent_type) {
    case "document":
      return note.document?.title || "Document";
    case "scripture":
      return note.scripture_reference?.book
        ? `${note.scripture_reference.book} ${note.scripture_reference.chapter}:${note.scripture_reference.verse_start}`
        : "Scripture";
    case "quote":
      return note.quote?.author ? `Quote by ${note.quote.author}` : "Quote";
    default:
      return "Loose note";
  }
}

function noteLink(note: Note) {
  switch (note.parent_type) {
    case "document":
      return note.parent_id ? `/documents/${note.parent_id}/edit` : `/notes/${note.id}`;
    case "scripture":
      return note.parent_id
        ? `/scripture-references/${note.parent_id}`
        : `/notes/${note.id}`;
    case "quote":
      return note.parent_id ? `/quotes/${note.parent_id}` : `/notes/${note.id}`;
    default:
      return `/notes/${note.id}`;
  }
}

function NoteIcon({ note }: { note: Note }) {
  const className = "h-4 w-4 text-muted-foreground";
  if (note.parent_type === "scripture") return <BookOpen className={className} />;
  if (note.parent_type === "quote") return <Quote className={className} />;
  if (note.parent_type === "document") return <FileText className={className} />;
  return <StickyNote className={className} />;
}

export default function NotesPage() {
  const [filterText, setFilterText] = useState("");
  const [noteType, setNoteType] = useState<NoteType>("all");

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["/api/notes"],
    queryFn: () => get<Note[]>("/notes"),
  });

  const visible = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    return notes
      .filter((note) => noteType === "all" || note.parent_type === noteType)
      .filter((note) => !needle || note.content.toLowerCase().includes(needle))
      .sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at).getTime() -
          new Date(a.updated_at || a.created_at).getTime(),
      );
  }, [notes, noteType, filterText]);

  const hasFilters = noteType !== "all" || filterText.trim().length > 0;

  return (
    <CollectionPage
      title="Notes"
      count={visible.length}
      noun="note"
      isLoading={isLoading}
      action={<AddNoteModal />}
      filters={
        <>
          <FilterInput
            value={filterText}
            onChange={setFilterText}
            placeholder="Filter notes…"
          />

          <Select
            value={noteType}
            onValueChange={(value: NoteType) => setNoteType(value)}
          >
            <SelectTrigger className="h-9 w-auto min-w-[9rem] gap-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(TYPE_LABELS) as NoteType[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {TYPE_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-muted-foreground"
              onClick={() => {
                setFilterText("");
                setNoteType("all");
              }}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </>
      }
    >
      {isLoading ? (
        <CollectionLoading label="Loading notes…" />
      ) : visible.length === 0 ? (
        <CollectionEmpty
          icon={<StickyNote className="h-10 w-10 text-muted-foreground" />}
          title={hasFilters ? "Nothing matches these filters." : "No notes yet."}
          hint={
            hasFilters
              ? "Try clearing a filter."
              : "Capture a thought from the editor, or add one here."
          }
        />
      ) : (
        <CollectionList>
          {visible.map((note) => (
            <CollectionRow
              key={note.id}
              href={noteLink(note)}
              icon={<NoteIcon note={note} />}
              title={noteHeadline(note)}
              subtitle={noteSource(note)}
              meta={formatDistanceToNow(new Date(note.updated_at || note.created_at), {
                addSuffix: true,
              })}
            />
          ))}
        </CollectionList>
      )}
    </CollectionPage>
  );
}
